/**
 * O tema da tela (`C-19`, ADR-055): o que a pessoa escolheu e o que o `<html>` mostra.
 *
 * São duas coisas, e a diferença importa. A **escolha** tem três valores — `escuro` (o
 * padrão), `claro` e `sistema` — e mora na conta da pessoa, na API. O **tema efetivo** tem
 * dois, `escuro` e `claro`, e é o que vai no `data-tema` do `<html>`: "igual ao sistema" é
 * resolvido aqui, e não no CSS, para os tokens existirem em dois blocos e não em três.
 *
 * O navegador guarda uma cópia da escolha (`cockpit.tema`), só para a tela de entrada — que
 * vem antes do login — não abrir num tema e trocar para outro. É uma preferência de tela,
 * não dado de ninguém: o token de acesso continua só em memória (ADR-009). O `public/tema.js`
 * lê a mesma chave antes da primeira pintura; `tests/tema.teste.ts` confere que os dois
 * concordam.
 */
import type { Identidade } from "./api/tipos";

export type Tema = Identidade["tema"];
export type TemaEfetivo = "escuro" | "claro";

export const TEMAS: readonly Tema[] = ["escuro", "claro", "sistema"];
export const TEMA_PADRAO: Tema = "escuro";
export const CHAVE_DO_TEMA = "cockpit.tema";

/** A cor da barra do navegador e da barra de status do celular, em cada tema: a do `topo`. */
export const COR_DA_BARRA: Record<TemaEfetivo, string> = {
  escuro: "#12161c",
  claro: "#f8f9fb",
};

const CONSULTA_DO_CLARO = "(prefers-color-scheme: light)";

export function ehTema(valor: unknown): valor is Tema {
  return (
    typeof valor === "string" && (TEMAS as readonly string[]).includes(valor)
  );
}

/** O sistema está no claro? Sem `matchMedia` (jsdom, navegador antigo), não: o escuro é o
 *  padrão do cockpit. */
export function sistemaNoClaro(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia(CONSULTA_DO_CLARO).matches
  );
}

export function temaEfetivo(
  tema: Tema,
  claroNoSistema = sistemaNoClaro(),
): TemaEfetivo {
  if (tema === "sistema") return claroNoSistema ? "claro" : "escuro";
  return tema;
}

/** A cópia do navegador; qualquer coisa que não seja um dos três temas vale o padrão. */
export function lerTemaGuardado(): Tema {
  try {
    const guardado = window.localStorage.getItem(CHAVE_DO_TEMA);
    return ehTema(guardado) ? guardado : TEMA_PADRAO;
  } catch {
    return TEMA_PADRAO;
  }
}

function guardarTema(tema: Tema): void {
  try {
    window.localStorage.setItem(CHAVE_DO_TEMA, tema);
  } catch {
    // Navegador sem armazenamento: a tela de entrada abre no escuro, e o resto vem da API.
  }
}

/** Pinta a tela no tema da escolha e guarda a cópia. Devolve o tema efetivo. */
export function aplicarTema(tema: Tema): TemaEfetivo {
  const efetivo = temaEfetivo(tema);
  const raiz = document.documentElement;
  raiz.dataset.tema = efetivo;
  raiz.dataset.escolha = tema;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", COR_DA_BARRA[efetivo]);
  document
    .querySelector('meta[name="color-scheme"]')
    ?.setAttribute("content", efetivo === "claro" ? "light" : "dark");
  guardarTema(tema);
  return efetivo;
}

/**
 * "Igual ao sistema" acompanha o sistema **enquanto** a tela está aberta: o Windows que passa
 * para o escuro às 18h leva o cockpit junto. Devolve a função que para de acompanhar.
 */
export function acompanharOSistema(aoMudar: () => void): () => void {
  if (typeof window.matchMedia !== "function") return () => {};
  const lista = window.matchMedia(CONSULTA_DO_CLARO);
  lista.addEventListener("change", aoMudar);
  return () => lista.removeEventListener("change", aoMudar);
}
